import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import HighStock from 'highcharts/highstock';
import type { Options as HighchartsOptions } from 'highcharts';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCNoData from 'highcharts/modules/no-data-to-display';
import moment from 'moment';
import { bindLineChartTooltipVisibility, lineChartHighchartsTooltipCfg } from '~/shared/utils/lineChartTooltip';
import { getLineChartExportMenuItems, registerLineChartExport } from '~/shared/utils/lineChartExport';
import { syncLineChartLegendTooltips } from '~/shared/utils/lineChartLegendTooltip';
import {
  syncLineChartSelectedSeriesFromChart,
  useLineChartLegendClickHandlers,
} from '~/shared/utils/lineChartSelectionSync';
import { plotBandOpacity } from '~/shared/constants';
import { hex2rgba } from '../../../../utils/colors/hex2rgba';
import {
  deriveNonNegativeAxisFloor,
  deriveAxisLimits,
  normalizePoint,
  readFiniteXExtentsFromAxis,
  xExtentsDiffer,
  pickXExtentsFromAfterSetExtremesEvent,
  shouldPersistUserXExtentsFromEvent,
  type PointLike,
} from '~/shared/utils/chartAxis';
import {
  applyRangeSelectorToChart,
  areChartXExtremesCanonicalForRangeIndex,
  DEFAULT_RANGE_SELECTOR_INDEX,
  extractSeriesXValues,
  MAX_RANGE_SELECTOR_INDEX,
  readMainAxisMaxForDurationPreset,
} from '~/shared/utils/timeWindow';
import { Root, HighchartContainer } from './IndicatorLineChart.styles';

HCExporting(HighStock);
HCNoData(HighStock);
HCOfflineExporting(HighStock);
HCAccessibility(HighStock);
registerLineChartExport(HighStock);

const EMPTY_STOCK_OPTIONS: HighchartsOptions = { series: [] };

HighStock.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function IndicatorLineChartBase({
  data,
  apiData,
  chartTitle,
  chartSubtitle = '',
  chartYtitle,
  exportFileName,
  prism,
  tooltipCfg,
  onTooltipVisibilityChange,
  doneHide,
  setDoneHide,
  smaValue,
  selectedRangeSelector,
  setSelectedRangeSelector,
  numberOfLines,
  yAxisRangeMode = 'padded-visible',
  yAxisPaddingRatio = 0.2,
  seriesDescriptionByName,
}: any) {
  const chartRef = useRef<any>(null);
  const [xRangeUserCustom, setXRangeUserCustom] = useState(false);
  const applyingRangePresetRef = useRef(false);
  const pendingDurationPresetAnchorRef = useRef<number | undefined>(undefined);
  const [selectedSeriesNames, setSelectedSeriesNames] = useState<string[] | null>(null);
  const savedUserXExtentsRef = useRef<{ min: number; max: number } | null>(null);

  const baseSeries = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((series: any) => series && series.name && !/^Navigator \d+$/.test(series.name));
  }, [data]);

  const baseSeriesNames = useMemo(() => baseSeries.map((series: any) => series.name), [baseSeries]);

  const normalizeSelection = useCallback(
    (selection: string[] | null | undefined) => {
      if (selection === null || selection === undefined) {
        return baseSeriesNames;
      }
      const selectionSet = new Set(selection);
      return baseSeriesNames.filter(name => selectionSet.has(name));
    },
    [baseSeriesNames],
  );

  const areSelectionsEqual = useCallback(
    (a: string[] | null | undefined, b: string[] | null | undefined) => {
      if (a === b) return true;
      const normalizedA = normalizeSelection(a);
      const normalizedB = normalizeSelection(b);
      if (normalizedA.length !== normalizedB.length) {
        return false;
      }
      return normalizedA.every((value, index) => normalizedB[index] === value);
    },
    [normalizeSelection],
  );

  const resolvedSelectedNames = useMemo(
    () => normalizeSelection(selectedSeriesNames),
    [normalizeSelection, selectedSeriesNames],
  );

  useEffect(() => {
    setDoneHide(false);
  }, [data, numberOfLines, setDoneHide]);

  const valuesBg = apiData?.highchartsConfig?.xAxis_custom_background_values;
  const colorsBg = apiData?.highchartsConfig?.xAxis_custom_background_colors;
  const labelsBg = apiData?.highchartsConfig?.xAxis_custom_background_labels;

  const plotBands = useMemo(() => {
    const bands =
      colorsBg &&
      colorsBg.map((color: any, i: number) => ({
        from: moment.utc(valuesBg[i]).valueOf(),
        to: moment.utc(valuesBg[i + 1]).valueOf(),
        color: hex2rgba(color, plotBandOpacity),
        label: {
          text: labelsBg?.[i],
          rotation: 90,
          align: 'left',
          useHtml: true,
          style: {
            color: '#000000',
          },
        },
      }));
    return bands;
  }, []);

  useEffect(() => {
    if (!baseSeries.length) {
      if (selectedSeriesNames !== null) {
        setSelectedSeriesNames(null);
      }
      return;
    }

    if (selectedSeriesNames === null) {
      return;
    }

    if (!Array.isArray(selectedSeriesNames)) {
      setSelectedSeriesNames(null);
      return;
    }

    if (selectedSeriesNames.length === 0) {
      return;
    }

    const baseNameSet = new Set(baseSeriesNames);
    const filtered = selectedSeriesNames.filter(name => baseNameSet.has(name));

    if (!filtered.length) {
      setSelectedSeriesNames(null);
      return;
    }

    if (filtered.length === selectedSeriesNames.length) {
      return;
    }

    setSelectedSeriesNames(filtered);
  }, [baseSeries, baseSeriesNames, selectedSeriesNames]);

  useEffect(() => {
    if (!baseSeries.length) return;
    if (selectedSeriesNames === null) return;
    if (!Array.isArray(selectedSeriesNames)) return;

    if (
      selectedSeriesNames.length === baseSeriesNames.length &&
      selectedSeriesNames.every(name => baseSeriesNames.includes(name))
    ) {
      setSelectedSeriesNames(null);
    }
  }, [baseSeries.length, baseSeriesNames, selectedSeriesNames]);

  const updateSelectedSeriesFromChart = useCallback(
    (chartInstance?: HighStock.Chart, revealedSeriesName?: string, allowCollapseToEmpty = false) => {
      const chart = chartInstance ?? chartRef?.current?.chart;
      if (!chart) return;

      syncLineChartSelectedSeriesFromChart({
        chart,
        baseSeriesNames,
        selectedSeriesNames,
        areSelectionsEqual,
        setSelectedSeriesNames,
        revealedSeriesName,
        allowCollapseToEmpty,
      });
    },
    [areSelectionsEqual, baseSeriesNames, selectedSeriesNames],
  );

  const { legendEventsItemClick } = useLineChartLegendClickHandlers({
    baseSeriesNames,
    selectedSeriesNames,
    setSelectedSeriesNames,
  });

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;
    updateSelectedSeriesFromChart(chart);
    // Only re-sync when the underlying series set changes, not on every selection update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSeries]);

  const preparedSeries = useMemo(() => {
    if (!Array.isArray(data)) return data;

    const selectionSet = new Set(resolvedSelectedNames);
    const isDefaultSelection = selectedSeriesNames === null;

    return data.map((series: any) => {
      if (!series || !series.name) {
        return { ...series };
      }
      if (/^Navigator \d+$/.test(series.name)) {
        return { ...series };
      }

      const isVisible = isDefaultSelection ? true : selectionSet.has(series.name);

      return {
        ...series,
        visible: isVisible,
        showInLegend: true,
        showInNavigator: series.showInNavigator !== false,
        step: false,
        ...(series.custom?.description ? { custom: { description: series.custom.description } } : {}),
      };
    });
  }, [data, resolvedSelectedNames, selectedSeriesNames]);

  const baseSeriesCount = baseSeries.length;

  const updateYAxisForVisibleWindow = useCallback(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;

    const yAxis = chart.yAxis?.[0];
    const xAxis = chart.xAxis?.[0];
    if (!yAxis || !xAxis) return;

    if (yAxisRangeMode !== 'padded-visible') {
      yAxis.setExtremes(undefined, undefined, false, false);
      chart.redraw();
      return;
    }

    const xExtremes = xAxis.getExtremes();
    const minX = xExtremes?.min;
    const maxX = xExtremes?.max;

    const values = chart.series
      .filter(
        (series: any) => series && series.name && !/^Navigator \d+$/.test(series.name) && series.visible !== false,
      )
      .flatMap((series: any) => {
        const renderedPoints = Array.isArray(series.points) ? (series.points as PointLike[]) : [];
        const sourcePoints =
          renderedPoints.length > 0
            ? renderedPoints
            : Array.isArray(series.options?.data)
              ? (series.options.data as PointLike[])
              : [];
        return sourcePoints
          .map((point: PointLike) => normalizePoint(point))
          .filter((point: { x: number; y: number } | null): point is { x: number; y: number } => point !== null)
          .filter(
            (point: { x: number; y: number }) =>
              !Number.isFinite(minX) || !Number.isFinite(maxX) || (point.x >= minX && point.x <= maxX),
          )
          .map((point: { x: number; y: number }) => point.y);
      })
      .filter((value: number) => Number.isFinite(value));

    const limits = deriveAxisLimits(values, yAxisPaddingRatio);
    if (typeof limits.min === 'number' && typeof limits.max === 'number') {
      yAxis.setExtremes(limits.min, limits.max, false, false);
    } else {
      yAxis.setExtremes(undefined, undefined, false, false);
    }
    chart.redraw();
  }, [yAxisPaddingRatio, yAxisRangeMode]);

  const lastAppliedRangeRef = useRef<{ smaValue: unknown; selectedRangeSelector: unknown } | null>(null);
  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;

    const last = lastAppliedRangeRef.current;
    const rangeChanged = !last || last.smaValue !== smaValue || last.selectedRangeSelector !== selectedRangeSelector;

    chart.redraw();

    if (rangeChanged) {
      setXRangeUserCustom(false);
      applyingRangePresetRef.current = true;
      const selectorChanged = !last || last.selectedRangeSelector !== selectedRangeSelector;
      const anchorFromButton = selectorChanged ? pendingDurationPresetAnchorRef.current : undefined;
      pendingDurationPresetAnchorRef.current = undefined;
      const anchorForApply = selectorChanged ? anchorFromButton : readMainAxisMaxForDurationPreset(chart);
      const applied = applyRangeSelectorToChart(chart, selectedRangeSelector, anchorForApply);
      if (applied) {
        lastAppliedRangeRef.current = { smaValue, selectedRangeSelector };
        savedUserXExtentsRef.current = null;
      }
      requestAnimationFrame(() => {
        applyingRangePresetRef.current = false;
      });
    } else {
      const xAxis = chart.xAxis?.[0];
      const saved = savedUserXExtentsRef.current;
      if (saved && xAxis) {
        requestAnimationFrame(() => {
          const cur = readFiniteXExtentsFromAxis(xAxis);
          if (cur && xExtentsDiffer(cur, saved)) {
            xAxis.setExtremes(saved.min, saved.max, false, false);
          }
        });
      }
    }
  }, [smaValue, selectedRangeSelector, chartRef, preparedSeries]);

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (!chart || xRangeUserCustom) return;

    const xValues = extractSeriesXValues(chart.series || []);
    if (!xValues.length) return;

    const xAxis = chart.xAxis?.[0];
    if (!xAxis || typeof xAxis.getExtremes !== 'function') return;

    const { min, max } = xAxis.getExtremes();
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    if (areChartXExtremesCanonicalForRangeIndex(chart, selectedRangeSelector, min, max)) return;

    applyingRangePresetRef.current = true;
    applyRangeSelectorToChart(chart, selectedRangeSelector);
    requestAnimationFrame(() => {
      applyingRangePresetRef.current = false;
    });
  }, [preparedSeries, selectedRangeSelector, xRangeUserCustom]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateYAxisForVisibleWindow();
    });

    return () => cancelAnimationFrame(rafId);
  }, [preparedSeries, selectedRangeSelector, updateYAxisForVisibleWindow]);

  useEffect(() => {
    const chart = chartRef?.current?.chart;
    const xAxis = chart?.xAxis?.[0];
    if (!chart || !xAxis) return;

    const handleAfterSetExtremes = (e: any) => {
      const picked = pickXExtentsFromAfterSetExtremesEvent(e);
      if (picked && shouldPersistUserXExtentsFromEvent(e, { applyingRangePreset: applyingRangePresetRef.current })) {
        savedUserXExtentsRef.current = picked;
      }
      const chartInstance = chartRef?.current?.chart;
      const evMin = e?.min;
      const evMax = e?.max;
      if (
        !applyingRangePresetRef.current &&
        chartInstance &&
        Number.isFinite(evMin) &&
        Number.isFinite(evMax) &&
        !areChartXExtremesCanonicalForRangeIndex(chartInstance, selectedRangeSelector, evMin, evMax)
      ) {
        setXRangeUserCustom(true);
      }
      requestAnimationFrame(() => {
        updateYAxisForVisibleWindow();
      });
    };

    HighStock.addEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    return () => {
      HighStock.removeEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    };
  }, [preparedSeries, selectedRangeSelector, updateYAxisForVisibleWindow]);

  const chartOptions = useMemo(() => {
    const chartMarginTop = chartSubtitle ? 108 : baseSeriesCount === 2 ? 94 : 84;
    let legendY: number;
    if (baseSeriesCount < 3) {
      legendY = chartSubtitle ? 20 : baseSeriesCount === 2 ? -10 : 20;
    } else {
      legendY = chartSubtitle ? -15 : -13;
    }
    const rangeSelectorY =
      baseSeriesCount < 3 ? (chartSubtitle ? -18 : baseSeriesCount === 2 ? 0 : 10) : chartSubtitle ? -19 : 10;

    const { floor: nonNegativeAxisFloor } =
      yAxisRangeMode !== 'padded-visible'
        ? deriveNonNegativeAxisFloor(
            Array.isArray(preparedSeries) ? (preparedSeries as Array<{ data?: PointLike[]; visible?: boolean }>) : [],
          )
        : { floor: undefined as number | undefined };
    return {
      custom: {
        seriesDescriptionByName: seriesDescriptionByName ?? {},
      },
      chart: {
        marginLeft: 76,
        marginRight: baseSeriesCount < 3 ? 25 : 148,
        marginTop: chartMarginTop,
        animation: false,
        custom: {
          seriesDescriptionByName: seriesDescriptionByName ?? {},
        },
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
        events: {
          load: function (this: HighStock.Chart) {
            syncLineChartLegendTooltips(this, tooltipCfg);
            bindLineChartTooltipVisibility(this, HighStock, onTooltipVisibilityChange);
          },
          redraw: function (this: HighStock.Chart) {
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
          render: function (this: HighStock.Chart) {
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
        },
      },
      xAxis: {
        type: 'datetime',
        ordinal: false,
        plotBands: plotBands,
      },
      rangeSelector: {
        buttonSpacing: 12,
        inputEnabled: false,
        selected: xRangeUserCustom ? null : (selectedRangeSelector ?? DEFAULT_RANGE_SELECTOR_INDEX),
        floating: true,
        labelStyle: { display: 'none' },
        x: 0,
        y: rangeSelectorY,
        buttonTheme: {
          r: 15,
          fill: 'transparent',
          height: 26,
          style: {
            fill: 'transparent',
            fontSize: 13,
            marginRight: 15,
            color: '#33485E',
            background: 'transparent',
            fontWeight: 500,
          },
          states: {
            hover: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 500,
              },
            },
            select: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 600,
              },
            },
          },
        },
        buttonPosition: {
          x: -48,
          y: -86,
        },
        buttons: [
          {
            type: 'month',
            count: 1,
            text: '1M',
            title: 'View 1 month',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(0);
              },
            },
          },
          {
            type: 'month',
            count: 3,
            text: '3M',
            title: 'View 3 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(1);
              },
            },
          },
          {
            type: 'month',
            count: 6,
            text: '6M',
            title: 'View 6 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(2);
              },
            },
          },
          {
            type: 'ytd',
            text: 'YTD',
            title: 'View year to date',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(3);
              },
            },
          },
          {
            type: 'year',
            count: 1,
            text: '1Y',
            title: 'View 1 year',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef?.current?.chart);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(4);
              },
            },
          },
          {
            type: 'all',
            text: 'Max',
            title: 'View all',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(MAX_RANGE_SELECTOR_INDEX);
              },
            },
          },
        ],
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof HighStock.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        buttons: {
          contextButton: {
            x: 0,
            y: 0,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: getLineChartExportMenuItems(baseSeriesCount),
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 20,
        y: 22,
        style: {
          fontSize: '18px',
          color: 'var(--color-gray-6)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: chartSubtitle,
        align: 'left',
        x: 20,
        y: 42,
        style: {
          color: 'var(--color-gray-6)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        floating: baseSeriesCount < 3,
        enabled: baseSeriesCount > 1,
        layout: baseSeriesCount < 3 ? 'horizontal' : 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: baseSeriesCount < 3 ? -4 : -10,
        y: legendY,
        maxHeight: 250,
        width: baseSeriesCount < 3 ? 'unset' : 110,
        reversed: baseSeriesCount > 2,
        events: {
          itemClick: legendEventsItemClick,
        },
      },
      yAxis: {
        width: 60,
        opposite: false,
        offset: 30,
        tickPixelInterval: 55,
        showFirstLabel: true,
        showLastLabel: true,
        labels: {
          y: 5,
          align: 'left',
          allowOverlap: true,
        },
        title: {
          text: chartYtitle,
          color: 'var(--color-gray-6)',
          x: -19,
        },
        plotLines: [
          {
            value: 0,
            width: 2,
            color: 'silver',
          },
        ],
        ...(nonNegativeAxisFloor !== undefined
          ? {
              floor: nonNegativeAxisFloor,
              softMin: nonNegativeAxisFloor,
              minPadding: 0,
            }
          : {}),
        ...(yAxisRangeMode === 'padded-visible'
          ? {
              startOnTick: false,
              endOnTick: false,
            }
          : {}),
      },
      plotOptions: {
        series: {
          selected: false,
          showInNavigator: true,
          animation: false,
          dataGrouping: { enabled: false },
          turboThreshold: 0,
          boostThreshold: 0,
          threshold: undefined,
          softThreshold: false,
          step: false,
          lineWidth: 2,
          line: {
            selected: false,
          },
          events: {
            afterAnimate: function (event: HighStock.SeriesAfterAnimateCallbackFunction) {
              const e = event as unknown as any;
              const chart = e.target.chart;
              const allSeries = chart?.series?.filter((s: any) => !/^Navigator \d+$/.test(s.name));
              if (!doneHide && allSeries?.length) {
                const hasExplicitSelection = Array.isArray(selectedSeriesNames);

                if (hasExplicitSelection) {
                  const selectionSet = new Set(selectedSeriesNames);
                  allSeries.forEach((s: any) => {
                    if (!s) return;
                    const shouldShow = selectionSet.size === 0 ? false : selectionSet.has(s.name);
                    s.setVisible(shouldShow, false);
                  });
                } else {
                  const totalSeries = allSeries.length;
                  const parsedLimit =
                    typeof numberOfLines === 'number' && Number.isFinite(numberOfLines) && numberOfLines > 0
                      ? Math.floor(numberOfLines)
                      : null;

                  const limitStartIndex =
                    parsedLimit !== null ? Math.max(totalSeries - Math.min(parsedLimit, totalSeries), 0) : 0;

                  allSeries.forEach((s: any, i: number) => {
                    if (!s) return;

                    let shouldShow = true;

                    if (parsedLimit !== null) {
                      shouldShow = i >= limitStartIndex;
                    } else if (totalSeries > 3) {
                      if (prism === 'Central Banks' && totalSeries > 8) {
                        shouldShow = i === 7 || i === 8;
                      } else {
                        shouldShow = i >= totalSeries - 1;
                      }
                    }

                    s.setVisible(shouldShow, false);
                  });
                }

                setDoneHide(true);
                updateSelectedSeriesFromChart(chart);
              }
            },
          },
        },
      },
      tooltip: {
        ...lineChartHighchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'rect',
        backgroundColor: '#fff',
        borderRadius: 5,
        borderColor: 'transparent',
        shadow: true,
      },
      lang: {
        noData: '',
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: 'var(--color-chart-no-data)',
        },
      },
      series: preparedSeries ?? data,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preparedSeries,
    tooltipCfg,
    onTooltipVisibilityChange,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    exportFileName,
    doneHide,
    numberOfLines,
    prism,
    selectedRangeSelector,
    xRangeUserCustom,
    setSelectedRangeSelector,
    updateSelectedSeriesFromChart,
    legendEventsItemClick,
    baseSeriesCount,
    selectedSeriesNames,
    resolvedSelectedNames,
    plotBands,
    setDoneHide,
    yAxisRangeMode,
    yAxisPaddingRatio,
    seriesDescriptionByName,
  ]);

  return (
    <Root>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={HighStock}
          constructorType={'stockChart'}
          options={chartOptions ?? EMPTY_STOCK_OPTIONS}
          ref={chartRef}
        />
      </HighchartContainer>
    </Root>
  );
}
